// ═══════════════════════════════════════════════════════════════════════════
// retentionStats.js — streak counter + time saved counter (mini-spec #5)
//
// Both stats live on `podcaster_profiles`:
//   • current_streak / longest_streak / last_build_date  (existed since v4.2)
//   • streak_freeze_count                                (added in 026)
//   • time_saved_minutes                                 (added in 026)
//
// All updates go through Supabase row UPDATEs gated by the user's own
// auth.uid() (existing RLS policy "own podcaster profile" covers this).
//
// Call `recordSuccessfulBuild(userId, action)` after every successful
// generation. Returns the new streak + time-saved values so the UI can
// flash a milestone celebration when a threshold is crossed (7/14/30/100
// days, or 10h/50h/100h/250h saved).
// ═══════════════════════════════════════════════════════════════════════════

import { supabase } from './supabase';
import { timeSavedFor } from './creditCosts';
import { getTier } from './planLimits';

const STREAK_MILESTONES      = [7, 14, 30, 60, 100, 365];
const TIME_SAVED_MILESTONES  = [10, 25, 50, 100, 250, 500, 1000]; // hours

function todayUTC() {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
}

function daysBetween(a, b) {
  return Math.round((todayUTC().getTime() - new Date(b).getTime()) / 86_400_000);
}

/**
 * Read the current streak + time-saved snapshot. Cheap read, safe to
 * call from any render path.
 */
export async function getRetentionStats(userId) {
  if (!supabase || !userId) return null;
  const { data, error } = await supabase
    .from('podcaster_profiles')
    .select('current_streak, longest_streak, last_build_date, streak_freeze_count, time_saved_minutes, plan')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) { console.error('getRetentionStats:', error); return null; }
  if (!data) return null;
  return {
    currentStreak:    data.current_streak    || 0,
    longestStreak:    data.longest_streak    || 0,
    lastBuildDate:    data.last_build_date,
    streakFreezeCount: data.streak_freeze_count || 0,
    timeSavedMinutes: data.time_saved_minutes || 0,
    timeSavedHours:   Math.floor((data.time_saved_minutes || 0) / 60),
    plan:             data.plan || 'free',
  };
}

/**
 * Called after a successful credit-spending action. Updates the streak
 * counter and time-saved tally in one round trip.
 *
 * Streak rules:
 *   • Same day as last build → no change
 *   • Next day              → +1 to current_streak
 *   • > 1 day gap, has freeze → consume freeze, keep streak
 *   • > 1 day gap, no freeze  → reset to 1
 *
 * Returns { current, longest, milestone, timeSavedMinutes } where
 * `milestone` is non-null when a notable threshold (7/14/30/100) was
 * just crossed — the UI uses that to pop a celebration toast.
 */
export async function recordSuccessfulBuild(userId, action) {
  if (!supabase || !userId) return null;
  const { data: row } = await supabase
    .from('podcaster_profiles')
    .select('current_streak, longest_streak, last_build_date, streak_freeze_count, time_saved_minutes, plan')
    .eq('user_id', userId)
    .maybeSingle();

  const today      = todayUTC().toISOString().slice(0, 10);
  const last       = row?.last_build_date || null;
  const prev       = row?.current_streak || 0;
  const longest    = row?.longest_streak || 0;
  const freezes    = row?.streak_freeze_count || 0;
  const minutesNow = row?.time_saved_minutes || 0;
  const tier       = getTier(row?.plan);

  let next = prev;
  let usedFreeze = false;

  if (!last) {
    // First build ever — start streak at 1.
    next = 1;
  } else {
    const gap = daysBetween(today, last);
    if (gap === 0) {
      // Already built today; streak unchanged.
      next = prev;
    } else if (gap === 1) {
      next = prev + 1;
    } else if (freezes > 0) {
      // Spend a freeze, keep the streak.
      next = prev + 1;
      usedFreeze = true;
    } else {
      // Streak broken — restart at 1.
      next = 1;
    }
  }

  const newLongest = Math.max(longest, next);
  const minutesAdded = timeSavedFor(action) || 0;
  const minutesNext  = minutesNow + minutesAdded;

  const updates = {
    current_streak:      next,
    longest_streak:      newLongest,
    last_build_date:     today,
    time_saved_minutes:  minutesNext,
  };
  if (usedFreeze) updates.streak_freeze_count = Math.max(0, freezes - 1);

  const { error } = await supabase
    .from('podcaster_profiles')
    .update(updates)
    .eq('user_id', userId);
  if (error) { console.warn('recordSuccessfulBuild update failed:', error.message); }

  // Detect milestone crossings.
  let streakMilestone = null;
  if (next > prev && STREAK_MILESTONES.includes(next)) streakMilestone = next;

  let timeSavedMilestoneHours = null;
  const prevHours = Math.floor(minutesNow / 60);
  const nextHours = Math.floor(minutesNext / 60);
  for (const h of TIME_SAVED_MILESTONES) {
    if (prevHours < h && nextHours >= h) { timeSavedMilestoneHours = h; break; }
  }

  return {
    current: next,
    longest: newLongest,
    streakMilestone,
    usedFreeze,
    freezesRemaining: usedFreeze ? Math.max(0, freezes - 1) : freezes,
    timeSavedMinutes: minutesNext,
    timeSavedHours: nextHours,
    timeSavedMilestoneHours,
    tierLabel: tier?.label || 'Free',
  };
}

/**
 * Grant a streak freeze. Called by Stripe webhook when a paid plan
 * activates / renews — Creator gets 1/mo, Studio gets 3/mo, Network
 * unlimited (we store 99 to keep the column an int rather than null).
 *
 * Client-side helper kept for parity with the server logic; the actual
 * grant should fire from the webhook to be the source of truth.
 */
export async function grantMonthlyFreezes(userId) {
  if (!supabase || !userId) return null;
  const stats = await getRetentionStats(userId);
  if (!stats) return null;
  const tier = getTier(stats.plan);
  let target = stats.streakFreezeCount;
  if (stats.plan === 'creator') target = Math.max(target, 1);
  else if (stats.plan === 'studio') target = Math.max(target, 3);
  else if (stats.plan === 'network') target = 99;
  if (target === stats.streakFreezeCount) return stats;
  await supabase
    .from('podcaster_profiles')
    .update({ streak_freeze_count: target })
    .eq('user_id', userId);
  return { ...stats, streakFreezeCount: target };
}

/** Format hours as a friendly compact string ("47h", "1d 4h", "12d"). */
export function formatTimeSaved(minutes) {
  const hours = Math.floor((minutes || 0) / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  const rest = hours % 24;
  if (rest === 0) return `${days}d`;
  return `${days}d ${rest}h`;
}
